import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoSettings, IoClose, IoExpand, IoContract, IoRemove, IoAdd } from 'react-icons/io5';
import './ReaderSettings.css';

const FONTS = [
  { id: 'default', label: 'DM Sans', family: 'var(--font-body)' },
  { id: 'serif', label: 'Playfair', family: 'var(--font-display)' },
  { id: 'georgia', label: 'Georgia', family: 'Georgia, serif' },
  { id: 'mono', label: 'Mono', family: "'Courier New', monospace" },
];

const THEMES = [
  { id: 'default', label: 'Clair', bg: '#faf6f0', text: '#2d2520', icon: '☀️' },
  { id: 'dark', label: 'Sombre', bg: '#1a1714', text: '#d4cfc7', icon: '🌙' },
  { id: 'sepia', label: 'Sépia', bg: '#f4e8d3', text: '#5c4a3a', icon: '📜' },
  { id: 'night', label: 'Nuit Bleue', bg: '#1a1e2e', text: '#a8b4c8', icon: '🌊' },
  { id: 'forest', label: 'Forêt', bg: '#1a2418', text: '#a8b8a0', icon: '🌲' },
  { id: 'rose', label: 'Rosé', bg: '#2a1a1e', text: '#c8a8a8', icon: '🌸' },
];

export default function ReaderSettings() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('plumly_reader_settings');
    return saved ? JSON.parse(saved) : {
      fontSize: 16,
      lineHeight: 1.8,
      font: 'default',
      theme: 'default',
    };
  });

  useEffect(() => {
    localStorage.setItem('plumly_reader_settings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

const applySettings = (s) => {
    const readerPage = document.querySelector('.reader-page');
    const font = FONTS.find(f => f.id === s.font);
    const theme = THEMES.find(t => t.id === s.theme);

    if (!readerPage || !theme) return;

    // Appliquer le thème à TOUTE la page
    readerPage.style.background = theme.bg;
    readerPage.style.color = theme.text;

    // Header
    const header = document.querySelector('.reader-header');
    if (header) {
      header.style.background = theme.bg;
      header.style.borderColor = theme.id === 'default' ? '' : 'rgba(128,128,128,0.15)';
    }

    // Tous les éléments textuels
    const textElements = readerPage.querySelectorAll('.reader-text, .reader-chapter-title, .reader-chapter-time, .reader-header-title, .reader-header-chapter, .reader-nav-progress, .reader-rating-label, .reader-author-note-text');
    textElements.forEach(el => {
      el.style.color = theme.text;
    });

    // Texte principal — taille, police, interligne
    const readerText = document.querySelector('.reader-text');
    if (readerText) {
      readerText.style.fontSize = s.fontSize + 'px';
      readerText.style.lineHeight = s.lineHeight;
      readerText.style.fontFamily = font ? font.family : 'var(--font-body)';
    }

    // Boutons du header
    const actions = readerPage.querySelectorAll('.reader-action, .reader-back');
    actions.forEach(el => {
      el.style.color = theme.text;
    });

    // Content
    const readerContent = document.querySelector('.reader-content');
    if (readerContent) {
      readerContent.style.background = theme.bg;
    }
  };

  // Ré-appliquer quand on navigue entre chapitres
  useEffect(() => {
    const observer = new MutationObserver(() => applySettings(settings));
    const target = document.querySelector('.reader-content');
    if (target) observer.observe(target, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [settings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  return (
    <>
      <motion.button
        className="reader-action"
        onClick={() => setIsOpen(!isOpen)}
        whileTap={{ scale: 0.9 }}
      >
        <IoSettings />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="rs-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              className="rs-panel"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
            >
              <div className="rs-header">
                <h3>Réglages de lecture</h3>
                <button className="rs-close" onClick={() => setIsOpen(false)}><IoClose /></button>
              </div>

              {/* Thème de lecture */}
              <div className="rs-setting">
                <label className="rs-label">🎨 Thème</label>
                <div className="rs-themes">
                  {THEMES.map(theme => (
                    <motion.button
                      key={theme.id}
                      className={'rs-theme-btn ' + (settings.theme === theme.id ? 'rs-theme-active' : '')}
                      onClick={() => updateSetting('theme', theme.id)}
                      whileTap={{ scale: 0.95 }}
                      style={{ background: theme.bg, color: theme.text }}
                    >
                      <span className="rs-theme-icon">{theme.icon}</span>
                      <span className="rs-theme-label">{theme.label}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Taille du texte */}
              <div className="rs-setting">
                <label className="rs-label">🔤 Taille du texte</label>
                <div className="rs-control">
                  <motion.button className="rs-btn" onClick={() => updateSetting('fontSize', Math.max(12, settings.fontSize - 1))} whileTap={{ scale: 0.9 }}><IoRemove /></motion.button>
                  <span className="rs-value">{settings.fontSize}px</span>
                  <motion.button className="rs-btn" onClick={() => updateSetting('fontSize', Math.min(28, settings.fontSize + 1))} whileTap={{ scale: 0.9 }}><IoAdd /></motion.button>
                </div>
              </div>

              {/* Interligne */}
              <div className="rs-setting">
                <label className="rs-label">↕ Interligne</label>
                <div className="rs-control">
                  <motion.button className="rs-btn" onClick={() => updateSetting('lineHeight', Math.max(1.2, +(settings.lineHeight - 0.1).toFixed(1)))} whileTap={{ scale: 0.9 }}><IoRemove /></motion.button>
                  <span className="rs-value">{settings.lineHeight}</span>
                  <motion.button className="rs-btn" onClick={() => updateSetting('lineHeight', Math.min(2.5, +(settings.lineHeight + 0.1).toFixed(1)))} whileTap={{ scale: 0.9 }}><IoAdd /></motion.button>
                </div>
              </div>

              {/* Police */}
              <div className="rs-setting">
                <label className="rs-label">✏️ Police</label>
                <div className="rs-fonts">
                  {FONTS.map(font => (
                    <motion.button
                      key={font.id}
                      className={'rs-font-btn ' + (settings.font === font.id ? 'rs-font-active' : '')}
                      onClick={() => updateSetting('font', font.id)}
                      whileTap={{ scale: 0.95 }}
                      style={{ fontFamily: font.family }}
                    >
                      {font.label}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Plein écran */}
              <motion.button className="rs-fullscreen-btn" onClick={toggleFullscreen} whileTap={{ scale: 0.97 }}>
                {isFullscreen ? <IoContract /> : <IoExpand />}
                {isFullscreen ? 'Quitter le plein écran' : 'Mode plein écran'}
              </motion.button>

              <button className="rs-reset" onClick={() => setSettings({ fontSize: 16, lineHeight: 1.8, font: 'default', theme: 'default' })}>
                Réinitialiser
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}